// Shared level-schema helpers.
//
// Two representations are used throughout the app:
//  - "2D shape": mirrors assets/levels/*.json in the jelly_match game repo —
//    candiesLayer/blockersLayer/overlaysLayer/etc are List<List<int>>. This is
//    what LevelEditor paints and what gets exported back to local JSON.
//  - "Firestore doc": the same data with every 2D layer flattened to a 1D,
//    row-major array (Firestore rejects arrays that directly contain arrays,
//    so a 2D grid can't be stored as-is — see zip-game-admin's "flatGrid" for
//    the same workaround).
//
// See docs/level-format.md in the jelly_match repo for the authoritative
// contract this mirrors (piece codes, goal types, HP defaults, etc).

export const COLOR_KEYS = ['1', '2', '3', '4', '5'];
export const LAYER_KEYS_2D = [
  'candiesLayer',
  'blockersLayer',
  'overlaysLayer',
  'blockerHpLayer',
  'overlayHpLayer',
  'boosterColorsLayer',
];
const FLAT_KEY_BY_LAYER = {
  candiesLayer: 'candiesFlat',
  blockersLayer: 'blockersFlat',
  overlaysLayer: 'overlaysFlat',
  blockerHpLayer: 'blockerHpFlat',
  overlayHpLayer: 'overlayHpFlat',
  boosterColorsLayer: 'boosterColorsFlat',
};

export function zeros(w, h) {
  return Array.from({ length: h }, () => Array(w).fill(0));
}

export function defaultCandies(w, h) {
  return Array.from({ length: h }, (_, r) => Array.from({ length: w }, (_, c) => ((r + c) % 5) + 1));
}

export function defaultLevel(levelId, chapter, w = 7, h = 7) {
  return {
    schemaVersion: 1,
    levelId,
    chapter,
    mode: 'objectives',
    width: w,
    height: h,
    moves: 20,
    targetScore: 0,
    starScores: [500, 1200, 2000],
    dropRates: { 1: 20, 2: 20, 3: 20, 4: 20, 5: 20 },
    goals: [{ target: 'candy', type: 1, count: 10 }],
    candiesLayer: defaultCandies(w, h),
    blockersLayer: zeros(w, h),
    overlaysLayer: zeros(w, h),
    blockerHpLayer: zeros(w, h),
    overlayHpLayer: zeros(w, h),
    boosterColorsLayer: zeros(w, h),
  };
}

function flatten(layer) {
  return layer.flat();
}

function unflatten(flat, w, h) {
  const out = [];
  for (let r = 0; r < h; r++) out.push(flat.slice(r * w, r * w + w));
  return out;
}

/** 2D-shape level -> Firestore-safe flattened document. */
export function toFirestoreDoc(level) {
  const doc = {
    schemaVersion: 1,
    levelId: level.levelId,
    chapter: level.chapter,
    mode: level.mode,
    width: level.width,
    height: level.height,
    moves: level.moves,
    targetScore: level.mode === 'score' ? level.targetScore : 0,
    starScores: level.starScores,
    dropRates: level.dropRates,
    goals: level.goals,
  };
  for (const key of LAYER_KEYS_2D) {
    doc[FLAT_KEY_BY_LAYER[key]] = flatten(level[key]);
  }
  return doc;
}

/** Firestore document -> 2D-shape level, ready for LevelEditor/export. */
export function fromFirestoreDoc(doc) {
  const { width, height } = doc;
  const level = {
    schemaVersion: 1,
    levelId: doc.levelId,
    chapter: doc.chapter ?? 1,
    mode: doc.mode || 'objectives',
    width,
    height,
    moves: doc.moves,
    targetScore: doc.targetScore || 0,
    starScores: doc.starScores || [500, 1200, 2000],
    dropRates: doc.dropRates || { 1: 20, 2: 20, 3: 20, 4: 20, 5: 20 },
    goals: doc.goals || [],
  };
  for (const key of LAYER_KEYS_2D) {
    const flat = doc[FLAT_KEY_BY_LAYER[key]];
    level[key] = flat ? unflatten(flat, width, height) : zeros(width, height);
  }
  return level;
}

/** 2D-shape level -> the exact JSON contract from docs/level-format.md. */
export function toLocalJson(level) {
  const out = {
    schemaVersion: 1,
    levelId: level.levelId,
    mode: level.mode,
    width: level.width,
    height: level.height,
    moves: level.moves,
  };
  if (level.mode === 'score') out.targetScore = level.targetScore;
  out.starScores = level.starScores;
  out.dropRates = level.dropRates;
  out.goals = level.goals;
  out.boosterColorsLayer = level.boosterColorsLayer;
  out.candiesLayer = level.candiesLayer;
  out.blockersLayer = level.blockersLayer;
  out.overlaysLayer = level.overlaysLayer;
  out.blockerHpLayer = level.blockerHpLayer;
  out.overlayHpLayer = level.overlayHpLayer;
  return out;
}

export function normalizeLevel(level) {
  const w = level.width,
    h = level.height;
  for (const key of LAYER_KEYS_2D) {
    if (!level[key]) level[key] = key === 'candiesLayer' ? defaultCandies(w, h) : zeros(w, h);
  }
  for (let r = 0; r < h; r++) {
    for (let c = 0; c < w; c++) {
      const piece = level.candiesLayer[r][c];
      const isColored = piece >= 6 && (piece <= 8 || piece === 10);
      if (isColored && !level.boosterColorsLayer[r][c]) {
        level.boosterColorsLayer[r][c] = ((r + c) % 5) + 1;
      }
      if (level.blockersLayer[r][c] > 0 && !level.blockerHpLayer[r][c]) {
        level.blockerHpLayer[r][c] = 1;
      }
      if (level.overlaysLayer[r][c] > 0 && !level.overlayHpLayer[r][c]) {
        level.overlayHpLayer[r][c] = 1;
      }
    }
  }
  return level;
}

export function goalAvailability(level, target, type) {
  let count = 0;
  const layer = target === 'blocker' ? level.blockersLayer : level.overlaysLayer;
  for (let r = 0; r < level.height; r++) {
    for (let c = 0; c < level.width; c++) {
      if (layer[r][c] === type) count++;
    }
  }
  return count;
}

/** Mirrors LevelData._validate in lib/models/level_data.dart. Returns an array of error strings. */
export function validateLevel(d) {
  const errors = [];
  const push = (cond, msg) => {
    if (!cond) errors.push(msg);
  };

  push(Number.isInteger(d.levelId) && d.levelId > 0, 'levelId phải là số nguyên dương.');
  push(Number.isInteger(d.moves) && d.moves > 0, 'moves phải là số nguyên dương.');
  push(Number.isInteger(d.width) && d.width >= 1 && d.width <= 12, 'width phải trong khoảng 1-12.');
  push(Number.isInteger(d.height) && d.height >= 1 && d.height <= 12, 'height phải trong khoảng 1-12.');
  push(['objectives', 'score', 'survival'].includes(d.mode), 'mode không hợp lệ.');
  if (d.mode === 'score') push(d.targetScore > 0, 'score mode cần targetScore dương.');

  const s = d.starScores || [];
  push(s.length === 3 && s[0] >= 0 && s[0] < s[1] && s[1] < s[2], 'starScores phải tăng dần và không âm.');

  const dr = d.dropRates || {};
  const total = Object.values(dr).reduce((a, b) => a + (Number(b) || 0), 0);
  push(Object.keys(dr).every((k) => COLOR_KEYS.includes(k)), 'dropRates chỉ được dùng key màu 1-5.');
  push(Object.values(dr).every((v) => Number(v) >= 0), 'dropRates không được âm.');
  push(total > 0, 'Tổng dropRates phải dương.');

  const maxType = { candy: 5, blocker: 1, overlay: 2 };
  push(Array.isArray(d.goals) && d.goals.length > 0, 'Cần ít nhất 1 goal.');
  (d.goals || []).forEach((g, i) => {
    const mt = maxType[g.target] || 0;
    push(g.count > 0 && g.type >= 1 && g.type <= mt, `goals[${i}] có target/type/count không hợp lệ.`);
    if (g.target !== 'candy') {
      const avail = goalAvailability(d, g.target, g.type);
      push(g.count <= avail, `goals[${i}] yêu cầu ${g.count} nhưng bàn chỉ có ${avail}.`);
    }
  });

  if (!(d.width > 0 && d.height > 0)) return errors;
  for (const key of LAYER_KEYS_2D) {
    const layer = d[key];
    if (!layer || layer.length !== d.height) {
      errors.push(`${key} thiếu hàng (cần ${d.height}).`);
      continue;
    }
    if (layer.some((row) => row.length !== d.width)) {
      errors.push(`${key} có hàng sai số cột (cần ${d.width}).`);
    }
  }
  if (errors.some((e) => e.includes('thiếu hàng') || e.includes('sai số cột'))) return errors;

  for (let r = 0; r < d.height; r++) {
    for (let c = 0; c < d.width; c++) {
      const piece = d.candiesLayer[r][c];
      const blocker = d.blockersLayer[r][c];
      const overlay = d.overlaysLayer[r][c];
      const bhp = d.blockerHpLayer[r][c];
      const ohp = d.overlayHpLayer[r][c];
      const color = d.boosterColorsLayer[r][c];
      const isColored = piece >= 6 && (piece <= 8 || piece === 10);

      if (!(piece >= -1 && piece <= 10)) errors.push(`Ô [${r}][${c}] candy=${piece} ngoài khoảng -1..10.`);
      if (piece === -1 && (blocker !== 0 || overlay !== 0)) errors.push(`Ô [${r}][${c}] là hole nhưng vẫn có blocker/overlay.`);
      if (blocker !== 0 && (piece !== 0 || overlay !== 0)) errors.push(`Ô [${r}][${c}] có crate nhưng ô không rỗng/còn overlay.`);
      if (blocker === 0 && bhp !== 0) errors.push(`Ô [${r}][${c}] có blockerHp nhưng không có blocker.`);
      if (blocker !== 0 && !(bhp >= 1 && bhp <= 3)) errors.push(`Ô [${r}][${c}] blockerHp phải 1-3.`);
      if (overlay === 0 && ohp !== 0) errors.push(`Ô [${r}][${c}] có overlayHp nhưng không có overlay.`);
      if (overlay !== 0 && !(ohp >= 1 && ohp <= 3)) errors.push(`Ô [${r}][${c}] overlayHp phải 1-3.`);
      if (isColored && !(color >= 1 && color <= 5)) errors.push(`Ô [${r}][${c}] booster cần màu 1-5.`);
      if (!isColored && color !== 0) errors.push(`Ô [${r}][${c}] có màu booster nhưng không phải booster màu.`);
    }
  }
  return errors.slice(0, 60);
}
